import React, { useState } from 'react';
import { ChevronDown, Coffee, Clock, Plus, Info, ShieldCheck, CheckCircle2, ClipboardCheck, Loader2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { BreakStatusPill } from '../../components/hr/break/ui/BreakUi';
import { formatDateTimeET } from '../../utils/formatters';
import { fmtT, fmtMinLong, unexplainedPeriods, totalUnexplained, REVIEW_STATUS } from './inactivitySummary';
import { ExplanationAudit } from './InactivityExplanationDialog';

// INACTIVITY-UI-FINAL-006 — three plain-language cards (Breaks Taken · Unexplained Inactivity · Actions).
// Every technical detail (sessions, timeline, coverage, boundary notes) lives behind "View Audit Details".
const RecordRows = ({ records, dotCls, testId }) => (
  <ul className="space-y-1.5 mt-2">
    {records.map((r) => (
      <li key={r.record_id || r.from} className="flex items-center gap-2 text-[13px] text-slate-700" data-testid={testId}>
        <span className={`inline-block h-2 w-2 rounded-full ${dotCls}`} />
        <span className="font-medium">{r.label}</span>
        <span className="text-slate-400">·</span>
        <span>{fmtT(r.from)} – {r.open_ended ? 'ongoing' : fmtT(r.to)}</span>
        <span className="text-slate-400">·</span>
        <span>{fmtMinLong(r.minutes)}</span>
      </li>
    ))}
  </ul>
);

const CardTitle = ({ children, cls }) => <div className={`text-[11px] font-bold uppercase tracking-wide ${cls}`}>{children}</div>;

export const EmployeeSummary = ({ emp, date, canReview, canExplain, onExplain, onReview, reviewing, children }) => {
  const [audit, setAudit] = useState(false);
  const periods = unexplainedPeriods(emp.gaps);
  const explainedCount = periods.filter((p) => p.explanation).length;
  const rs = REVIEW_STATUS[emp.review_status] || REVIEW_STATUS.needs_review;
  const breaks = emp.breaks_taken || [];
  const downtime = emp.downtime_recorded || [];
  return (
    <div className="bg-slate-50/70 border-t border-slate-100 px-4 py-4 space-y-3" data-testid={`emp-summary-${emp.user_id}`}>
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.3fr_0.85fr] gap-4">
        {/* Breaks Taken */}
        <div className="rounded-xl border border-emerald-100 bg-emerald-50/60 p-5 flex gap-4" data-testid="breaks-taken">
          <div className="h-11 w-11 shrink-0 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center"><Coffee className="h-5 w-5" /></div>
          <div className="min-w-0 flex-1">
            <CardTitle cls="text-slate-700">Breaks Taken</CardTitle>
            <div className="text-[30px] font-bold text-slate-900 leading-tight mt-1" data-testid="breaks-taken-count">{breaks.length}</div>
            {breaks.length ? <RecordRows records={breaks} dotCls="bg-emerald-500" testId="breaks-taken-item" /> : <div className="text-[13px] text-slate-500 mt-1">No breaks recorded</div>}
            {downtime.length > 0 && (
              <div className="mt-3" data-testid="downtime-recorded">
                <CardTitle cls="text-slate-600">Downtime Recorded · {downtime.length}</CardTitle>
                <RecordRows records={downtime} dotCls="bg-sky-500" testId="downtime-recorded-item" />
              </div>
            )}
          </div>
        </div>
        {/* Unexplained Inactivity */}
        <div className={`rounded-xl border p-5 flex gap-4 ${periods.length ? 'border-red-100 bg-red-50/60' : 'border-emerald-100 bg-emerald-50/60'}`} data-testid="unexplained-inactivity">
          <div className={`h-11 w-11 shrink-0 rounded-full flex items-center justify-center ${periods.length ? 'bg-red-100 text-red-500' : 'bg-emerald-100 text-emerald-600'}`}><Clock className="h-5 w-5" /></div>
          <div className="min-w-0 flex-1">
            <CardTitle cls={periods.length ? 'text-red-600' : 'text-slate-700'}>Unexplained Inactivity</CardTitle>
            <div className={`text-[22px] font-bold leading-tight mt-1 ${periods.length ? 'text-red-600' : 'text-emerald-600'}`} data-testid="unexplained-total">
              {periods.length ? `${periods.length} period${periods.length === 1 ? '' : 's'} · ${fmtMinLong(totalUnexplained(periods))}` : 'None'}
            </div>
            {periods.length > 0 && (
              <div className={`mt-1 inline-flex items-center gap-1.5 text-[11.5px] font-semibold rounded-full px-2.5 py-0.5 ${explainedCount ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`} data-testid="explained-count">
                <CheckCircle2 className="h-3.5 w-3.5" /> Explained {explainedCount} of {periods.length} period{periods.length === 1 ? '' : 's'}
              </div>
            )}
            <ol className="mt-2 space-y-1.5">
              {periods.map((p, i) => (
                <li key={p.from} className="grid grid-cols-[20px_1fr_auto] items-center gap-3 text-[13px] text-slate-700 max-w-md" data-testid="unexplained-period">
                  <span className="h-5 w-5 rounded-full bg-red-100 text-red-600 text-[10.5px] font-bold flex items-center justify-center">{i + 1}</span>
                  <span className="flex items-center gap-2 flex-wrap">{fmtT(p.from)} – {fmtT(p.to)}
                    {p.ongoing && <span className="text-[10.5px] font-semibold rounded-full px-2 py-0.5 bg-orange-100 text-orange-700">Ongoing</span>}
                    {p.promptUnanswered && <span className="text-[10.5px] text-red-600 font-medium">prompt not answered</span>}
                    {p.explanation && (
                      <span className="inline-flex items-center gap-1 text-[10.5px] font-semibold rounded-full px-2 py-0.5 bg-emerald-100 text-emerald-700 cursor-help"
                        title={`${p.explanation.explanation}\n— ${p.explanation.updated_by_name} (${p.explanation.updated_by_role}) · ${formatDateTimeET(p.explanation.updated_at)}`} data-testid="period-explained-tag">
                        <CheckCircle2 className="h-3 w-3" /> Explained
                      </span>
                    )}
                  </span>
                  <span className="font-semibold text-slate-800 text-right tabular-nums">{fmtMinLong(p.minutes)}</span>
                </li>
              ))}
            </ol>
            {explainedCount > 0 && (
              <ul className="mt-2 space-y-1 border-t border-red-100/70 pt-2" data-testid="explanation-notes">
                {periods.filter((p) => p.explanation).map((p) => (
                  <li key={p.from} className="text-[12px] text-slate-600 flex gap-2" data-testid="explanation-note">
                    <span className="font-semibold tabular-nums text-slate-700 shrink-0">{fmtT(p.from)}</span>
                    <span className="min-w-0"><span className="italic">“{p.explanation.explanation}”</span> <span className="text-slate-400">— {p.explanation.updated_by_name}, {formatDateTimeET(p.explanation.updated_at)}</span></span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
        {/* Actions */}
        <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="employee-actions">
          <div className="flex items-center justify-between gap-2">
            <CardTitle cls="text-slate-700">Actions</CardTitle>
            {periods.length > 0 && <BreakStatusPill tone={rs.tone} testId="employee-review-status">{rs.label}</BreakStatusPill>}
          </div>
          {canExplain ? (
            <Button className="w-full mt-3 h-10 text-[13px] bg-blue-600 hover:bg-blue-700 shadow-sm transition-transform duration-150 hover:-translate-y-px" data-testid="add-explanation-btn"
              disabled={!periods.length} onClick={() => onExplain?.(emp)}>
              <Plus className="h-4 w-4 mr-1.5" /> {explainedCount ? 'Add / Edit Explanation' : 'Add Explanation'}
            </Button>
          ) : <div className="text-[12.5px] text-slate-500 mt-3">Contact HR/Admin to add an explanation.</div>}
          {canReview && periods.length > 0 && (
            <Button variant="outline" className="w-full mt-2 h-10 text-[13px] border-emerald-200 text-emerald-700 hover:bg-emerald-50" data-testid="mark-employee-reviewed-btn"
              disabled={reviewing || emp.review_status === 'reviewed'} onClick={() => onReview?.(emp)}>
              {reviewing ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <ClipboardCheck className="h-4 w-4 mr-1.5" />}
              {emp.review_status === 'reviewed' ? 'Reviewed' : emp.review_status === 'updated_since_review' ? 'Mark as Reviewed again' : 'Mark as Reviewed'}
            </Button>
          )}
          {emp.review && (
            <div className="mt-2 text-[11.5px] text-slate-500" data-testid="employee-review-meta">Reviewed by {emp.review.reviewed_by_name} · {formatDateTimeET(emp.review.reviewed_at)}</div>
          )}
          <div className="mt-3 flex items-start gap-2 rounded-lg bg-sky-50 border border-sky-100 px-3 py-2.5 text-[12px] text-slate-600">
            <Info className="h-4 w-4 text-sky-500 shrink-0 mt-0.5" /> Explanations document the inactivity; marking as reviewed records that HR has evaluated it. Durations are never changed.
          </div>
        </div>
      </div>
      {/* Audit details — collapsed by default */}
      <div className="rounded-xl border border-slate-200 bg-white" data-testid="audit-details-panel">
        <button type="button" className="w-full flex items-center gap-3 px-4 py-3 text-left" data-testid="audit-details-toggle" onClick={() => setAudit((v) => !v)}>
          <ShieldCheck className="h-[18px] w-[18px] text-slate-400 shrink-0" />
          <span className="flex-1 min-w-0">
            <span className="block text-[13px] font-semibold text-slate-800">View Audit Details</span>
            <span className="block text-[11.5px] text-slate-400">Attendance sessions, inactivity timeline, explanation history, and system details (for troubleshooting)</span>
          </span>
          <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${audit ? 'rotate-180' : ''}`} />
        </button>
        {audit && <div className="border-t border-dashed border-slate-200 px-4 pb-3" data-testid="audit-details">{children}<ExplanationAudit emp={emp} /></div>}
      </div>
    </div>
  );
};
